import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Globe,
  KeyRound,
  Link2,
  Lock,
  Radio,
  Server,
  Shield,
  ShieldCheck,
} from 'lucide-react';
import {
  AUTH_METHOD_OPTIONS,
  ENVIRONMENT_OPTIONS,
  IDP_OPTIONS,
  MCP_PUBLIC_URL,
  SINGLETON_ENDPOINT_ID,
  SINGLETON_ENDPOINT_SLUG,
  authMethodLabel,
  buildMcpUrl,
} from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import type { AuthMethod, McpEndpoint, McpEnvironment, SecuritySwitches } from '../governedMcpTypes';
import { EnvBadge, StatusBadge } from '../components/StatusBadges';
import { GatewayDrawer, HorizontalStepper, ToggleRow, fieldClass, ghostBtn, labelClass, primaryBtn } from '../components/GatewayUi';

type EndpointStep = 'identity' | 'connection' | 'authentication' | 'security' | 'review';

const STEPS: { id: EndpointStep; label: string; hint: string }[] = [
  { id: 'identity', label: 'Identity', hint: 'Name and environment' },
  { id: 'connection', label: 'Connection', hint: 'URL and transport' },
  { id: 'authentication', label: 'Authentication', hint: 'Universal Token' },
  { id: 'security', label: 'Security', hint: 'Governance switches' },
  { id: 'review', label: 'Review', hint: 'Save host settings' },
];

const DEFAULT_SECURITY: SecuritySwitches = {
  requireAuthenticatedUsers: true,
  requireRegisteredClients: true,
  propagateEnterpriseIdentity: true,
  enforceSelficPermissions: true,
  enableOutputGovernance: true,
  enableSensitiveDataMasking: true,
  enableCompleteAuditLogging: true,
  applyRateLimits: true,
  rateLimitPerMinute: 60,
};

const ENV_HINT: Record<McpEnvironment, string> = {
  development: 'Sandbox traffic. Never production data.',
  test: 'Pre-production. Controlled datasets only.',
  production: 'Live enterprise traffic. Highest scrutiny.',
};

const AUTH_HINT: Record<AuthMethod, string> = {
  universal_token: 'Selfic-issued token for MCP clients. Available now.',
  oauth_oidc: 'Enterprise users via OIDC. Required for Claude, ChatGPT, Copilot.',
  api_key: 'Non-human agents only. Still mapped through Selfic.',
  mtls: 'Certificate-bound clients. Pair with registered MCP clients.',
};

function blankEndpoint(): McpEndpoint {
  return {
    id: SINGLETON_ENDPOINT_ID,
    name: 'Selfic MCP',
    description: '',
    environment: 'production',
    slug: SINGLETON_ENDPOINT_SLUG,
    url: MCP_PUBLIC_URL,
    transport: 'streamable-http',
    compatibilitySse: false,
    authMethod: 'universal_token',
    identityProvider: 'selfic_sso',
    oidc: {
      issuerUrl: 'https://acme.okta.com/oauth2/default',
      clientId: '',
      scopes: 'openid profile mcp.invoke',
      audience: 'selfic-mcp',
    },
    enabled: true,
    security: { ...DEFAULT_SECURITY },
    credentialVersion: 1,
    lastRotatedAt: new Date().toISOString(),
    requestVolume: 0,
    lastActivityAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'Current administrator',
  };
}

function ComingSoonBadge({ isLight }: { isLight: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
        isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-300'
      }`}
    >
      <Clock size={10} />
      Coming soon
    </span>
  );
}

function SelectCard({
  selected,
  title,
  hint,
  onClick,
  isLight,
  badge,
  comingSoon,
}: {
  selected: boolean;
  title: string;
  hint?: string;
  onClick?: () => void;
  isLight: boolean;
  badge?: React.ReactNode;
  comingSoon?: boolean;
}) {
  const disabled = comingSoon || !onClick;
  return (
    <button
      type="button"
      onClick={comingSoon ? undefined : onClick}
      disabled={disabled}
      className={`text-left rounded-xl border p-4 transition-all ${
        comingSoon
          ? isLight
            ? 'border-neutral-200 bg-neutral-50 opacity-70 cursor-not-allowed'
            : 'border-neutral-800 bg-neutral-900/50 opacity-60 cursor-not-allowed'
          : selected
            ? isLight
              ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200'
              : 'border-indigo-500 bg-indigo-500/10 ring-1 ring-indigo-500/30'
            : isLight
              ? 'border-neutral-200 bg-white hover:border-indigo-400/40'
              : 'border-neutral-800 bg-neutral-900/70 hover:border-indigo-400/40'
      }`}
    >
      <span className="flex items-start justify-between gap-2">
        <span className={`text-sm font-semibold ${comingSoon ? (isLight ? 'text-neutral-500' : 'text-neutral-400') : isLight ? 'text-neutral-900' : 'text-white'}`}>
          {title}
        </span>
        {comingSoon ? <ComingSoonBadge isLight={isLight} /> : badge}
      </span>
      {hint ? (
        <span className={`block text-xs mt-1.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{hint}</span>
      ) : null}
    </button>
  );
}

export default function EndpointFormDrawer({
  open,
  initial,
  isLight,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: McpEndpoint | null;
  isLight: boolean;
  onClose: () => void;
  onSave: (endpoint: McpEndpoint) => void;
}) {
  const [form, setForm] = useState<McpEndpoint>(blankEndpoint);
  const [step, setStep] = useState<EndpointStep>('identity');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      const next = initial ? { ...initial, security: { ...initial.security }, oidc: { ...initial.oidc } } : blankEndpoint();
      const methodComingSoon = AUTH_METHOD_OPTIONS.find((item) => item.value === next.authMethod)?.comingSoon;
      if (methodComingSoon) next.authMethod = 'universal_token';
      setForm(next);
      setStep('identity');
      setError(null);
      setCopied(false);
      logGovernedMcp('app', 'endpoint_popup_open', { edit: Boolean(initial), authMethod: next.authMethod });
    }
  }, [open, initial]);

  const url = useMemo(() => buildMcpUrl(form.slug), [form.slug]);
  const stepIndex = STEPS.findIndex((item) => item.id === step);
  const card = isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900/70';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const titleCls = isLight ? 'text-neutral-900' : 'text-white';

  const patchSecurity = (update: Partial<SecuritySwitches>) => {
    setForm((prev) => ({ ...prev, security: { ...prev.security, ...update } }));
  };

  const validateStep = (id: EndpointStep): string | null => {
    if (id === 'identity' && !form.name.trim()) return 'Endpoint name is required.';
    if (id === 'connection' && !form.slug.trim()) return 'Endpoint slug is required.';
    if (id === 'authentication' && form.authMethod === 'oauth_oidc' && !form.oidc.issuerUrl.trim()) {
      return 'OIDC issuer URL is required.';
    }
    return null;
  };

  const goNext = () => {
    const message = validateStep(step);
    if (message) {
      setError(message);
      logGovernedMcp('warning', 'endpoint_step_blocked', { step, message });
      return;
    }
    setError(null);
    setStep(STEPS[Math.min(stepIndex + 1, STEPS.length - 1)].id);
  };

  const goBack = () => {
    setError(null);
    setStep(STEPS[Math.max(stepIndex - 1, 0)].id);
  };

  const submit = () => {
    const blocked = validateStep('identity') || validateStep('connection') || validateStep('authentication');
    if (blocked) {
      setError(blocked);
      logGovernedMcp('warning', 'endpoint_submit_blocked', { message: blocked });
      return;
    }
    logGovernedMcp('app', 'endpoint_submit', { slug: SINGLETON_ENDPOINT_SLUG, env: form.environment });
    onSave({
      ...form,
      id: SINGLETON_ENDPOINT_ID,
      slug: SINGLETON_ENDPOINT_SLUG,
      url: MCP_PUBLIC_URL,
    });
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      logGovernedMcp('app', 'endpoint_url_copied', { url });
    } catch {
      logGovernedMcp('error', 'endpoint_url_copy_failed');
    }
  };

  const securityCount = [
    form.security.requireAuthenticatedUsers,
    form.security.requireRegisteredClients,
    form.security.propagateEnterpriseIdentity,
    form.security.enforceSelficPermissions,
    form.security.enableOutputGovernance,
    form.security.enableSensitiveDataMasking,
    form.security.enableCompleteAuditLogging,
    form.security.applyRateLimits,
  ].filter(Boolean).length;

  return (
    <GatewayDrawer
      open={open}
      eyebrow="Governed MCP / Host"
      icon={<Server size={18} className="text-indigo-500" />}
      title={form.name || 'MCP host settings'}
      subtitle="One locked URL for every registered client. Tenant and user come from the MCP JWT."
      status={{ label: form.enabled ? 'Enabled' : 'Disabled', tone: form.enabled ? 'success' : 'neutral' }}
      isLight={isLight}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <span className={`mr-auto text-xs ${muted}`}>
            Step {stepIndex + 1} of {STEPS.length} · {STEPS[stepIndex].label}
          </span>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          {stepIndex > 0 ? (
            <button type="button" className={ghostBtn(isLight)} onClick={goBack}>
              <ChevronLeft size={14} /> Back
            </button>
          ) : null}
          {step === 'review' ? (
            <button type="button" className={primaryBtn()} onClick={submit}>
              Save host settings
            </button>
          ) : (
            <button type="button" className={primaryBtn()} onClick={goNext}>
              Continue <ChevronRight size={14} />
            </button>
          )}
        </>
      }
    >
      <div className="space-y-6">
        <HorizontalStepper
          steps={STEPS}
          currentIndex={stepIndex}
          onSelect={(index) => {
            setError(null);
            setStep(STEPS[index].id);
          }}
          isLight={isLight}
        />
        <section className={`border rounded-2xl p-5 shadow-sm ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}>
          {error ? (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              {error}
            </div>
          ) : null}

          {step === 'identity' ? (
            <div className="space-y-5 max-w-3xl">
              <div>
                <h3 className={`text-base font-semibold ${titleCls}`}>Host identity</h3>
                <p className={`text-sm mt-1 ${muted}`}>A display name only. The public MCP URL is fixed for this tenant.</p>
              </div>
              <div>
                <label className={labelClass(isLight)}>Host name</label>
                <input
                  className={fieldClass(isLight)}
                  value={form.name}
                  placeholder="Selfic MCP"
                  onChange={(event) => {
                    setForm((prev) => ({ ...prev, name: event.target.value }));
                  }}
                />
              </div>
              <div>
                <label className={labelClass(isLight)}>Description</label>
                <textarea
                  className={fieldClass(isLight)}
                  rows={3}
                  value={form.description}
                  placeholder="What this gateway is for, and who may use it."
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </div>
              <div>
                <p className={labelClass(isLight)}>Environment</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {ENVIRONMENT_OPTIONS.map((option) => (
                    <SelectCard
                      key={option.value}
                      isLight={isLight}
                      selected={form.environment === option.value}
                      title={option.label}
                      hint={ENV_HINT[option.value]}
                      badge={<EnvBadge environment={option.value} />}
                      onClick={() => setForm((prev) => ({ ...prev, environment: option.value }))}
                    />
                  ))}
                </div>
              </div>
              <div className={`rounded-xl border px-4 ${card}`}>
                <ToggleRow
                  label="Host enabled"
                  hint="When disabled, the MCP host refuses handshake and invocation."
                  checked={form.enabled}
                  onChange={(enabled) => setForm((prev) => ({ ...prev, enabled }))}
                  isLight={isLight}
                />
              </div>
            </div>
          ) : null}

          {step === 'connection' ? (
            <div className="space-y-5 max-w-3xl">
              <div>
                <h3 className={`text-base font-semibold ${titleCls}`}>Connection</h3>
                <p className={`text-sm mt-1 ${muted}`}>Share this URL with approved MCP clients. It never bypasses Selfic authorization.</p>
              </div>
              <div>
                <label className={labelClass(isLight)}>Slug</label>
                <input className={`${fieldClass(isLight)} font-mono`} value={SINGLETON_ENDPOINT_SLUG} readOnly />
                <p className={`text-xs mt-1 ${muted}`}>Locked. The public path is always /mcp.</p>
              </div>
              <div className={`rounded-xl border p-4 ${card}`}>
                <p className={labelClass(isLight)}>MCP URL</p>
                <div className="flex gap-2">
                  <input className={`${fieldClass(isLight)} font-mono`} value={url} readOnly />
                  <button type="button" className={ghostBtn(isLight)} onClick={() => void copyUrl()}>
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <div>
                <p className={labelClass(isLight)}>Transport</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <SelectCard
                    isLight={isLight}
                    selected
                    title="Streamable HTTP"
                    hint="Primary MCP transport. Always on."
                  />
                  <SelectCard
                    isLight={isLight}
                    selected={form.compatibilitySse}
                    title="SSE compatibility"
                    hint="Optional fallback for older MCP clients."
                    onClick={() => setForm((prev) => ({ ...prev, compatibilitySse: !prev.compatibilitySse }))}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {step === 'authentication' ? (
            <div className="space-y-5 max-w-3xl">
              <div>
                <h3 className={`text-base font-semibold ${titleCls}`}>Authentication</h3>
                <p className={`text-sm mt-1 ${muted}`}>
                  Use a Selfic Universal Token for now. Other methods stay available for later. Never trust a user id supplied by the AI client.
                </p>
              </div>
              <div>
                <p className={labelClass(isLight)}>Authentication method</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {AUTH_METHOD_OPTIONS.map((option) => (
                    <SelectCard
                      key={option.value}
                      isLight={isLight}
                      selected={form.authMethod === option.value}
                      title={option.label}
                      hint={AUTH_HINT[option.value]}
                      comingSoon={option.comingSoon}
                      onClick={option.comingSoon ? undefined : () => setForm((prev) => ({ ...prev, authMethod: option.value }))}
                    />
                  ))}
                </div>
              </div>
              <div>
                <p className={labelClass(isLight)}>Enterprise identity provider</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {IDP_OPTIONS.map((option) => (
                    <SelectCard
                      key={option.value}
                      isLight={isLight}
                      selected={false}
                      title={option.label}
                      comingSoon={option.comingSoon ?? true}
                    />
                  ))}
                </div>
              </div>
              {form.authMethod === 'oauth_oidc' ? (
                <div className={`rounded-xl border p-4 space-y-3 ${card}`}>
                  <p className={`text-xs font-semibold ${titleCls}`}>OAuth / OIDC</p>
                  <div>
                    <label className={labelClass(isLight)}>Issuer URL</label>
                    <input
                      className={fieldClass(isLight)}
                      value={form.oidc.issuerUrl}
                      onChange={(event) => setForm((prev) => ({ ...prev, oidc: { ...prev.oidc, issuerUrl: event.target.value } }))}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass(isLight)}>Client ID</label>
                      <input
                        className={fieldClass(isLight)}
                        value={form.oidc.clientId}
                        onChange={(event) => setForm((prev) => ({ ...prev, oidc: { ...prev.oidc, clientId: event.target.value } }))}
                      />
                    </div>
                    <div>
                      <label className={labelClass(isLight)}>Audience</label>
                      <input
                        className={fieldClass(isLight)}
                        value={form.oidc.audience}
                        onChange={(event) => setForm((prev) => ({ ...prev, oidc: { ...prev.oidc, audience: event.target.value } }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass(isLight)}>Scopes</label>
                    <input
                      className={fieldClass(isLight)}
                      value={form.oidc.scopes}
                      onChange={(event) => setForm((prev) => ({ ...prev, oidc: { ...prev.oidc, scopes: event.target.value } }))}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 'security' ? (
            <div className="space-y-5 max-w-3xl">
              <div>
                <h3 className={`text-base font-semibold ${titleCls}`}>Governance switches</h3>
                <p className={`text-sm mt-1 ${muted}`}>
                  These apply to every handshake and invocation on this endpoint. Capabilities can tighten further, not loosen.
                </p>
              </div>
              <div className={`rounded-xl border px-4 ${card}`}>
                <ToggleRow
                  label="Require authenticated users"
                  hint="No anonymous MCP sessions."
                  checked={form.security.requireAuthenticatedUsers}
                  onChange={(requireAuthenticatedUsers) => patchSecurity({ requireAuthenticatedUsers })}
                  isLight={isLight}
                />
                <ToggleRow
                  label="Require registered / trusted MCP clients"
                  hint="Unknown AI applications cannot discover tools."
                  checked={form.security.requireRegisteredClients}
                  onChange={(requireRegisteredClients) => patchSecurity({ requireRegisteredClients })}
                  isLight={isLight}
                />
                <ToggleRow
                  label="Propagate authenticated enterprise user identity"
                  hint="Never trust a user id supplied by the AI client."
                  checked={form.security.propagateEnterpriseIdentity}
                  onChange={(propagateEnterpriseIdentity) => patchSecurity({ propagateEnterpriseIdentity })}
                  isLight={isLight}
                />
                <ToggleRow
                  label="Enforce Selfic user-level permissions"
                  hint="The same rights apply in UI, API, workflow, and MCP."
                  checked={form.security.enforceSelficPermissions}
                  onChange={(enforceSelficPermissions) => patchSecurity({ enforceSelficPermissions })}
                  isLight={isLight}
                />
                <ToggleRow
                  label="Enable output governance"
                  checked={form.security.enableOutputGovernance}
                  onChange={(enableOutputGovernance) => patchSecurity({ enableOutputGovernance })}
                  isLight={isLight}
                />
                <ToggleRow
                  label="Enable sensitive-data masking"
                  checked={form.security.enableSensitiveDataMasking}
                  onChange={(enableSensitiveDataMasking) => patchSecurity({ enableSensitiveDataMasking })}
                  isLight={isLight}
                />
                <ToggleRow
                  label="Enable complete audit logging"
                  checked={form.security.enableCompleteAuditLogging}
                  onChange={(enableCompleteAuditLogging) => patchSecurity({ enableCompleteAuditLogging })}
                  isLight={isLight}
                />
                <ToggleRow
                  label="Apply rate limits"
                  checked={form.security.applyRateLimits}
                  onChange={(applyRateLimits) => patchSecurity({ applyRateLimits })}
                  isLight={isLight}
                />
              </div>
              {form.security.applyRateLimits ? (
                <div className="max-w-xs">
                  <label className={labelClass(isLight)}>Requests per minute</label>
                  <input
                    type="number"
                    min={1}
                    className={fieldClass(isLight)}
                    value={form.security.rateLimitPerMinute}
                    onChange={(event) => patchSecurity({ rateLimitPerMinute: Number(event.target.value) || 1 })}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 'review' ? (
            <div className="space-y-4 max-w-3xl">
              <h3 className={`text-base font-semibold ${titleCls}`}>Review host settings</h3>
              <dl className={`grid sm:grid-cols-2 gap-3 text-sm rounded-xl border p-4 ${card}`}>
                <div>
                  <dt className={muted}>Name</dt>
                  <dd className={titleCls}>{form.name || '—'}</dd>
                </div>
                <div>
                  <dt className={muted}>Environment</dt>
                  <dd><EnvBadge environment={form.environment} /></dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className={muted}>MCP URL</dt>
                  <dd className={`font-mono text-xs break-all ${titleCls}`}>{url}</dd>
                </div>
                <div>
                  <dt className={muted}>Transport</dt>
                  <dd className={titleCls}>Streamable HTTP{form.compatibilitySse ? ' + SSE' : ''}</dd>
                </div>
                <div>
                  <dt className={muted}>Auth</dt>
                  <dd className={titleCls}>
                    {authMethodLabel(form.authMethod)}
                    {form.authMethod !== 'universal_token'
                      ? ` · ${IDP_OPTIONS.find((item) => item.value === form.identityProvider)?.label}`
                      : ''}
                  </dd>
                </div>
                <div>
                  <dt className={muted}>Status</dt>
                  <dd><StatusBadge status={form.enabled} /></dd>
                </div>
                <div>
                  <dt className={muted}>Governance switches</dt>
                  <dd className={titleCls}>{securityCount} of 8 on</dd>
                </div>
              </dl>
              <p className={`text-xs rounded-lg px-3 py-2 ${isLight ? 'bg-indigo-50 text-indigo-800' : 'bg-indigo-500/10 text-indigo-200'}`}>
                This does not publish capabilities. Register a client, then publish tools onto {MCP_PUBLIC_URL}.
              </p>
            </div>
          ) : null}
        </section>

        <aside className={`border rounded-2xl p-5 shadow-sm ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}>
          <p className={`text-sm font-semibold mb-3 ${titleCls}`}>Remote MCP URL</p>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Server size={18} className="text-indigo-400" />
              <span className={`text-sm font-semibold truncate ${titleCls}`}>{form.name || 'Selfic MCP'}</span>
            </div>
            <div className="flex flex-wrap gap-1 mb-3">
              <EnvBadge environment={form.environment} />
              <StatusBadge status={form.enabled} />
            </div>
            <ul className={`space-y-2.5 text-xs ${muted}`}>
              <li className="flex gap-2">
                <Link2 size={13} className="mt-0.5 shrink-0" />
                <span className="font-mono break-all">{url.replace('https://', '')}</span>
              </li>
              <li className="flex gap-2">
                <Radio size={13} className="mt-0.5 shrink-0" />
                Streamable HTTP{form.compatibilitySse ? ' + SSE' : ''}
              </li>
              <li className="flex gap-2">
                <KeyRound size={13} className="mt-0.5 shrink-0" />
                {authMethodLabel(form.authMethod)}
              </li>
              {form.authMethod !== 'universal_token' ? (
                <li className="flex gap-2">
                  <Globe size={13} className="mt-0.5 shrink-0" />
                  {IDP_OPTIONS.find((item) => item.value === form.identityProvider)?.label}
                </li>
              ) : null}
              <li className="flex gap-2">
                <Lock size={13} className="mt-0.5 shrink-0" />
                {form.security.requireAuthenticatedUsers ? 'Users must authenticate' : 'Anonymous allowed'}
              </li>
              <li className="flex gap-2">
                <ShieldCheck size={13} className="mt-0.5 shrink-0" />
                {form.security.requireRegisteredClients ? 'Registered clients only' : 'Open client discovery'}
              </li>
              <li className="flex gap-2">
                <Shield size={13} className="mt-0.5 shrink-0" />
                {securityCount} governance switches on
              </li>
            </ul>
          </div>
          <p className={`text-[11px] italic mt-4 ${muted}`}>AI clients request. Selfic decides and governs execution.</p>
        </aside>
      </div>
    </GatewayDrawer>
  );
}
